const YES_NO = (yesLevel, noLevel, unsure = false) => [
  {id: 'no', label: 'No', level: noLevel},
  {id: 'yes', label: 'Yes', level: yesLevel},
  ...(unsure ? [{id: 'unsure', label: 'Not sure', level: 'unknown'}] : []),
];

export const AGE_BANDS = [
  {
    id: 'child',
    label: 'A child aged 6 or under',
    description: 'Includes baby teeth and the first adult teeth coming through.',
  },
  {
    id: 'older',
    label: 'Aged 7 or older',
    description: 'Older children, teenagers and adults.',
  },
];

const CHILD_STEPS = [
  {
    id: 'habits',
    title: 'Everyday habits',
    intro: 'Five questions about fluoride, food and dental visits.',
    questions: [
      {
        id: 'fluoride',
        prompt: 'Does the child get fluoride?',
        hint: 'Through drinking water, supplements, toothpaste, or applications at the dentist.',
        short: 'Fluoride',
        options: YES_NO('low', 'moderate'),
      },
      {
        id: 'sugar',
        prompt: 'When does the child have sugary food or drink?',
        hint: 'Including juice, fizzy and carbonated drinks, energy drinks and medicines containing sugar.',
        short: 'Sugary food and drink',
        options: [
          {id: 'meals', label: 'Mainly at mealtimes', level: 'low'},
          {id: 'between', label: 'Often, or over long stretches between meals', level: 'moderate'},
          {
            id: 'bottle',
            label: 'In a bottle or sippy cup at bedtime, anything other than water',
            level: 'high',
          },
        ],
      },
      {
        id: 'programs',
        prompt: 'Is the child enrolled in a government assistance programme?',
        hint: 'For example WIC, Head Start, Medicaid or SCHIP. The ADA form includes this as a risk factor.',
        short: 'Government assistance programme',
        options: YES_NO('high', 'low'),
      },
      {
        id: 'family',
        prompt: 'Has a parent, carer or sibling had a cavity recently?',
        short: 'Cavities in the family',
        options: [
          {id: 'none', label: 'Not in the last 24 months', level: 'low'},
          {id: 'mid', label: 'Between 7 and 23 months ago', level: 'moderate'},
          {id: 'recent', label: 'In the last 6 months', level: 'high'},
          {id: 'unsure', label: 'Not sure', level: 'unknown'},
        ],
      },
      {
        id: 'dental-home',
        prompt: 'Does the child have a regular dentist?',
        hint: 'Somewhere they are a registered patient and go back to.',
        short: 'A regular dentist',
        options: YES_NO('low', 'moderate'),
      },
    ],
  },
  {
    id: 'health',
    title: 'General health',
    questions: [
      {
        id: 'special-needs',
        prompt: 'Does the child have a condition or disability that makes daily tooth brushing difficult?',
        hint: 'Developmental, physical, medical or mental, whether the child brushes or someone helps them.',
        short: 'Help needed with daily brushing',
        options: YES_NO('high', 'low'),
      },
    ],
  },
  {
    id: 'teeth',
    title: 'Teeth and gums',
    intro: 'If you are not certain, say so. It will come back as something to ask the dentist.',
    questions: [
      {
        id: 'cavities',
        prompt: 'Has the child had a cavity or a filling in the last 24 months?',
        short: 'Recent cavities or fillings',
        options: YES_NO('high', 'low', true),
      },
      {
        id: 'early-decay',
        prompt: 'Has a dentist found early decay in the last 24 months?',
        hint: 'Early decay is a white or chalky spot that has not yet become a hole.',
        short: 'Early decay found',
        options: YES_NO('high', 'low', true),
      },
      {
        id: 'missing',
        prompt: 'Has the child lost any teeth because of decay?',
        short: 'Teeth lost to decay',
        options: YES_NO('high', 'low', true),
      },
      {
        id: 'plaque',
        prompt: 'Can you see plaque on the teeth?',
        hint: 'A soft, pale film along the gumline, most visible before brushing.',
        short: 'Visible plaque',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'appliance',
        prompt: 'Does the child wear a dental or orthodontic appliance?',
        hint: 'Fixed or removable, such as a space maintainer or an early aligner.',
        short: 'Dental appliance',
        options: YES_NO('moderate', 'low'),
      },
      {
        id: 'saliva',
        prompt: 'Does the child seem to produce enough saliva?',
        hint: 'A mouth that is often dry is the thing to watch for.',
        short: 'Saliva',
        options: [
          {id: 'normal', label: 'Seems normal', level: 'low'},
          {id: 'dry', label: 'Their mouth is often dry', level: 'high'},
          {id: 'unsure', label: 'Not sure', level: 'unknown'},
        ],
      },
    ],
  },
];

const OLDER_STEPS = [
  {
    id: 'habits',
    title: 'Everyday habits',
    intro: 'Four questions about fluoride, food and dental visits.',
    questions: [
      {
        id: 'fluoride',
        prompt: 'Do you get fluoride?',
        hint: 'Through drinking water, supplements, toothpaste, or applications at the dentist.',
        short: 'Fluoride',
        options: YES_NO('low', 'moderate'),
      },
      {
        id: 'sugar',
        prompt: 'When do you have sugary food or drink?',
        hint: 'Including juice, fizzy and carbonated drinks, energy drinks and medicines containing sugar.',
        short: 'Sugary food and drink',
        options: [
          {id: 'meals', label: 'Mainly at mealtimes', level: 'low'},
          {id: 'between', label: 'Often, or over long stretches between meals', level: 'high'},
        ],
      },
      {
        id: 'family',
        prompt: 'Has a parent, carer or sibling had a cavity recently?',
        hint: 'The ADA form asks this for patients aged 6 to 14.',
        short: 'Cavities in the family',
        options: [
          {id: 'none', label: 'Not in the last 24 months', level: 'low'},
          {id: 'mid', label: 'Between 7 and 23 months ago', level: 'moderate'},
          {id: 'recent', label: 'In the last 6 months', level: 'high'},
          {id: 'unsure', label: 'Not sure', level: 'unknown'},
        ],
      },
      {
        id: 'dental-home',
        prompt: 'Do you have a regular dentist you see for check-ups?',
        short: 'A regular dentist',
        options: YES_NO('low', 'moderate'),
      },
    ],
  },
  {
    id: 'health',
    title: 'General health',
    intro: 'Health and medication both affect how much protection your saliva gives you.',
    questions: [
      {
        id: 'special-needs',
        prompt: 'Do you have a condition or disability that makes daily brushing and cleaning difficult?',
        short: 'Help needed with daily cleaning',
        options: [
          {id: 'no', label: 'No', level: 'low'},
          {id: 'teen', label: 'Yes, and I am aged 6 to 14', level: 'high'},
          {id: 'adult', label: 'Yes, and I am over 14', level: 'moderate'},
        ],
      },
      {
        id: 'chemo',
        prompt: 'Are you having, or have you had, chemotherapy or radiation therapy?',
        short: 'Chemotherapy or radiation therapy',
        options: YES_NO('high', 'low'),
      },
      {
        id: 'eating',
        prompt: 'Do you have an eating disorder?',
        short: 'Eating disorder',
        options: YES_NO('moderate', 'low'),
      },
      {
        id: 'meds',
        prompt: 'Do you take medication that dries your mouth?',
        hint: 'More than 400 medicines can, including antihistamines, decongestants, antidepressants and some blood pressure medicines.',
        short: 'Medication that dries the mouth',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'substance',
        prompt: 'Do you use recreational drugs, or drink heavily?',
        short: 'Drug or alcohol use',
        options: YES_NO('moderate', 'low'),
      },
    ],
  },
  {
    id: 'teeth',
    title: 'Teeth and gums',
    intro:
      'Some of these are things a dentist spots. If you are not certain, say so and it will come back as something to ask about.',
    questions: [
      {
        id: 'cavities',
        prompt: 'How many cavities or fillings have you had in the last 36 months?',
        short: 'Cavities or fillings in the last 3 years',
        options: [
          {id: 'none', label: 'None', level: 'low'},
          {id: 'few', label: 'One or two', level: 'moderate'},
          {id: 'many', label: 'Three or more', level: 'high'},
          {id: 'unsure', label: 'Not sure', level: 'unknown'},
        ],
      },
      {
        id: 'missing',
        prompt: 'Have you lost a tooth to decay in the last 36 months?',
        short: 'Teeth lost to decay',
        options: YES_NO('high', 'low', true),
      },
      {
        id: 'plaque',
        prompt: 'Can you see plaque on your teeth?',
        hint: 'A soft, pale film along the gumline, most visible before brushing.',
        short: 'Visible plaque',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'morphology',
        prompt: 'Do any of your teeth have deep grooves or an unusual shape that makes them hard to clean?',
        short: 'Teeth that are hard to clean',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'interproximal',
        prompt: 'Do you have one or more fillings between your teeth?',
        short: 'Fillings between teeth',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'roots',
        prompt: 'Are any root surfaces exposed?',
        hint: 'Where the gum has receded and the darker root shows below the enamel.',
        short: 'Exposed root surfaces',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'margins',
        prompt: 'Do any fillings have rough edges or gaps, or does food catch between your teeth?',
        short: 'Rough fillings or food catching',
        options: YES_NO('moderate', 'low', true),
      },
      {
        id: 'appliance',
        prompt: 'Do you wear braces, a retainer or another dental appliance?',
        short: 'Braces or an appliance',
        options: YES_NO('moderate', 'low'),
      },
      {
        id: 'dry-mouth',
        prompt: 'Do you have a severely dry mouth?',
        short: 'Severe dry mouth',
        options: YES_NO('high', 'low', true),
      },
    ],
  },
];

export const RISK_STEPS = {
  child: CHILD_STEPS,
  older: OLDER_STEPS,
};

export function scoreRisk(band, answers) {
  let level = 'low';
  const drivers = [];
  const unknowns = [];

  for (const step of RISK_STEPS[band]) {
    for (const question of step.questions) {
      const option = question.options.find((o) => o.id === answers[question.id]);
      if (!option) continue;

      if (option.level === 'unknown') {
        unknowns.push(question.short);
        continue;
      }
      if (option.level === 'high') {
        level = 'high';
        drivers.push(`${question.short}: ${option.label.toLowerCase()}`);
      } else if (option.level === 'moderate') {
        if (level !== 'high') level = 'moderate';
        drivers.push(`${question.short}: ${option.label.toLowerCase()}`);
      }
    }
  }

  return {level, drivers, unknowns};
}

export const RESULTS = {
  low: {
    title: 'Low risk right now',
    body: 'Nothing in your answers points to a raised risk of decay today. Risk changes, so it is worth checking again if your health, medication or habits change.',
    next: [
      'Keep brushing twice a day and cleaning between your teeth daily',
      'Keep your regular check-ups',
      'Keep sugary food and drink to mealtimes',
    ],
  },
  moderate: {
    title: 'Moderate risk',
    body: 'Some of your answers are linked to a higher chance of decay. None of them is an emergency, and most respond well to small changes plus a conversation at your next visit.',
    next: [
      'Mention these answers at your next check-up',
      'Ask whether a fluoride toothpaste or rinse with more fluoride would help',
      'Cut back on sugary food and drink between meals',
      'Brush twice a day and clean between your teeth daily',
    ],
  },
  high: {
    title: 'High risk',
    body: 'At least one of your answers is strongly linked to decay. That does not mean you have a cavity. It means a dentist should take a look sooner rather than later, and there is a lot that can be done to bring the risk down.',
    next: [
      'Book a dental examination rather than waiting for your next routine visit',
      'Take these answers with you, they are what a dentist would ask',
      'Ask about high-fluoride products, sealants, and treatment for dry mouth',
      'Brush twice a day, clean between your teeth daily, and keep sugar to mealtimes',
    ],
  },
};

export const ADA_CREDIT =
  'Risk factors and scoring adapted from the American Dental Association Caries Risk Assessment forms (ages 0 to 6 and over 6). The wording here is ours and written for patients, so this is not the ADA form and is not a substitute for it.';

export const ASSESSMENT_DISCLAIMER =
  'This check is a conversation starter, not a diagnosis. It cannot see your teeth, and it does not replace an examination by a dentist, who may reach a different conclusion after looking.';
